import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class Expense extends Base implements EntityType {
    expenseId: uuid;
    userId?: uuid | null;
    payee?: uuid | null;
    description?: string | null;
    expenseDate: Date;
    expenseType: uuid;
    excludeFromInvoice: boolean;
    quantity: number;
    cost: number;
    reimbursable: boolean;
    taxable: boolean;
    amountPaid: number;
    invoiceId?: uuid | null;
    constructor(doc: typeof Expense | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            expenseId: genUUID(),
            expenseDate: null,
            expenseType: null,
            quantity: null,
            cost: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "expense";
        // @ts-ignore
        this.expenseId = doc._expenseId || doc.expenseId || doc.expenseid || genUUID();
        // @ts-ignore
        this.userId = doc._userId || doc.userId || doc.userid || null;
        // @ts-ignore
        this.payee = doc._payee || doc.payee || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.expenseDate = DateUtil.timestampOnly(doc._expenseDate) || DateUtil.timestampOnly(doc.expenseDate) || DateUtil.timestampOnly(doc.expensedate) || null;
        // @ts-ignore
        this.expenseType = doc._expenseType || doc.expenseType || doc.expensetype || null;
        // @ts-ignore
        this.excludeFromInvoice = util.defaultBoolean([doc.excludeFromInvoice, doc._excludeFromInvoice, doc.excludefrominvoice, false]);
        // @ts-ignore
        this.quantity = util.coalesce(util.parseFloat(doc._quantity), util.parseFloat(doc.quantity), util.parseFloat(doc.quantity), null);
        // @ts-ignore
        this.cost = util.coalesce(util.parseFloat(doc._cost), util.parseFloat(doc.cost), util.parseFloat(doc.cost), null);
        // @ts-ignore
        this.reimbursable = util.defaultBoolean([doc.reimbursable, doc._reimbursable, doc.reimbursable, true]);
        // @ts-ignore
        this.taxable = util.defaultBoolean([doc.taxable, doc._taxable, doc.taxable, false]);
        // @ts-ignore
        this.amountPaid = util.coalesce(util.parseFloat(doc._amountPaid), util.parseFloat(doc.amountPaid), util.parseFloat(doc.amountpaid), 0.0);
        // @ts-ignore
        this.invoiceId = doc._invoiceId || doc.invoiceId || doc.invoiceid || null;
    }
    get id(): uuid { return this.expenseId; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for this expense"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.expenseId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): Expense[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'expense',
    dataArea: 'Billing',
    attachedTo: {parents: ['matter'], via: 'entity', onDelete: 'delete', onReplace: 'move', blockWhen: {nonEmpty: 'invoiceId'}},
    table: 'EXPENSE',
    comment: 'A client-billable disbursement incurred on behalf of a matter or sale, such as court costs, filing fees, deposition costs, or advance client costs. Expenses carry a categorized type, quantity, per-unit cost, and reimbursability flag, and are tracked for inclusion on client invoices.',
    fields: {
        expenseId: {type: 'uuid', required: true, pk: true, default: '__UUID__', comment: 'Unique ID for this expense'},

        /* Expense information */
        userId: {type: 'uuid', entityType: 'user', deleteStrategy: 'preserve', comment: 'User that incurred the expense'},
        payee: {type: 'uuid', entityType: 'contact', comment: 'What contact was this expense was paid to?'},
        description: {type: 'string', comment: 'What the expense was for (if not obvious from who)?'},
        expenseDate: {type: 'timestamp', required: true, comment: 'When this expense was incurred'},
        expenseType: {
            type: 'uuid',
            entityType: 'lookup',
            lookupPaths: ['matter:expense:category'],

            // Experimental field to describe additional data in lookup structure.
            // These provide DEFAULTS only.  The actual value can be overridden using the reimburse and taxable fields
            additionalData: {
                templateNotesForInvoice: {type: 'text', name: 'Template Notes for Invoice', default: false}, // text, not string!
                // reimbursable: {type: 'boolean', name: 'Reimbursable?', default: true},
                // taxable: {type: 'boolean', name: 'Taxable?', default: false}
            },
            required: true,
            comment: 'Expense type'
        },
        excludeFromInvoice: {
            type: 'boolean',
            required: false,
            comment: 'Should this expense be excluded from the invoice?'
        },
        quantity: {type: 'float', required: true, comment: 'How many units of this item'},
        cost: {type: 'money', required: true, comment: 'Per unit price'},
        reimbursable: {type: 'boolean', default: true, required: false, comment: 'Should this expense be reimbursed?'},
        taxable: {type: 'boolean', default: false, required: false, comment: 'Is this expense taxable?'},
        amountPaid: {type: 'money', default: 0.0, comment: 'Amount of the cost considered paid'},
        invoiceId: {type: 'uuid', entityType: 'invoice', comment: 'Invoice associated with this expense'}
    },
    indexes: [
        {name: 'notDeleted', fn: t => t.status !== 'D'},
    ],
    maps: [
        {name: 'matterId', keysFn: e => e.entityId, presentFn: (e, k) => e.entityId === k && !e.excludeFromInvoice},
        {name: 'matterIdBillable', keysFn: e => e.entityId, presentFn: (e, k) => e.entityId === k && !e.excludeFromInvoice && e.reimbursable},
        {name: 'invoiceId', keysFn: e => e.invoiceId, presentFn: (e, k) => e.invoiceId === k},
        {name: 'userId', keysFn: t => t.userId, presentFn: (t, k) => t.userId === k},
        {name: 'payee', keysFn: t => t.payee, presentFn: (t, k) => t.payee === k},
        {name: 'expenseType', keysFn: t => t.expenseType, presentFn: (t, k) => t.expenseType === k},
    ]
});
    }
}
export default Expense;
